import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { dispatch, RootState, store } from '../../redux/store';
import { changeActivePageIndex, changePageFilter, changePagePath, clearPage } from '../../redux/actions/PageActions';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import ComponentName from '../../constant/ComponentName';
import FolderService from '../../service/FolderService';
import { initialPagePath } from '../../redux/reducers/PageReducer';
import Breadcrumb from '../../components/Breadcrumb';
import Icon from '../../icons/Icon';
import LanguageUtil from '../../util/LanguageUtil';
import RoleHelper from '../../RoleHelper';
import ItemToolsMenu from '../../components/ItemToolsMenu';
import FilterUtil from '../../util/FilterUtil';
import ValueFactory from '../../util/ValueFactory';
import _ from 'lodash';
import AchListViewerComponent from '../../components/ach/AchListViewerComponent';
import AchClient, { AchItem } from '../../client/ach/AchClient';
import AchService from '../../service/AchService';
import CollectionNames from '../../constant/CollectionNames';
import { query, subscribe } from '@trlogic/client-js';
import { Dialog } from 'primereact/dialog';
import AchFileUpload from '../../components/ach/AchFileUpload';
import MessageUtil from '../../util/MessageUtil';
import ServiceContainer from '../../config/ServiceContainer';
import { ServiceNames } from '../../constant/ServiceNames';
import { CustomPaginatorProps } from '../../components/custom/CustomPaginator';
import GatewayContainer from '../../config/GatewayContainer';
import GatewayNames from '../../constant/GatewayNames';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import QueryBuilder = query.QueryBuilder;
import Filter = query.Filter;
import empty = query.empty;
import Order = query.Order;

class AchContainer extends Component<Props, State> {
  public state: State = new State();
  private readonly achService: AchService = ServiceContainer.getInstance().get(ServiceNames.ACH);
  private readonly achClient: AchClient = GatewayContainer.getInstance().get(GatewayNames.ACH_CLIENT);

  private achSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private onSortChange = (order: Order[]) => {};

  public render() {
    if (!RoleHelper.canView('ACH')) {
      return <div>No Permission</div>;
    }

    const { filter, achItems, loading, importDialogVisible, uploading, rows, currentPage, totalRecord } = this.state;
    const { component } = this.props;
    const selectedComponentIds = component?.selectedComponentIds || [];
    const itemsIsSelected = selectedComponentIds.length > 0;

    return (
      <div className={'flex flex-col w-full h-full overflow-y-hidden'}>
        <Breadcrumb />
        <div className={'flex w-full shrink-0 2xl:px-5 px-4 pt-5 gap-x-5 overflow-x-hidden'}>
          <div className='w-[80px] shrink-0 h-[80px] flex justify-center items-center rounded-[50%] bg-[#EFF2FE]'>
            <Icon icon={'sidebar-ach'} size={30} className={'hidden'} color={'#728EFF'} />
          </div>
          <div className={'flex flex-col gap-y-1 overflow-x-scroll items-center justify-center'}>
            <span className={'text-4xl text-black font-normal'}>
              {LanguageUtil.get({
                id: 'pages.ach.title',
                defaultMessage: 'Automated Clearing House',
              })}
            </span>
          </div>
        </div>
        <div className={'flex justify-between bg-white items-center w-full 2xl:px-5 px-4'}>
          <ItemToolsMenu
            onChangeFilter={this.onChangeFilter}
            filter={filter}
            importVisible={true}
            importFunc={this.openImportDialog}
            component={ComponentName.ACH}
            componentLabel={LanguageUtil.get({ id: 'common.label.ach', defaultMessage: 'ACH' })}
            items={achItems}
            itemsIsSelected={itemsIsSelected}
          />
        </div>
        {this.renderImportDialog()}
        <div className={'flex flex-col w-full flex-1 min-h-0'}>
          <AchListViewerComponent
            achItems={achItems}
            openAch={() => {}}
            views={[]}
            currentModal={null}
            onSortChange={this.onSortChange}
            permissions={{}}
            loading={loading}
            onFileUploaded={this.loadAchFiles}
            router={this.props.router}
            rows={rows}
            totalRecord={totalRecord}
            currentPage={currentPage}
            onPageChange={this.onPageChange}
          />
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    const {
      router: { location },
      page,
      changePagePath,
      changeActivePageIndex,
    } = this.props;
    const initial = initialPagePath[ComponentName.ACH];

    const hasDetailPath = page.pagePath.length > 1 && page.pagePath[page.pagePath.length - 1].type === 'componentId';

    if (hasDetailPath) {
      const basePath = page.pagePath[0];
      changePagePath([basePath]);
      changeActivePageIndex(0);
    } else if (page.pagePath.length == 0 || page.pagePath[0].title != initial?.title) {
      await FolderService.calculate('WITHOUT_FOLDER', ComponentName.ACH, location.pathname, store.getState().page);
    }

    await this.prepareFilterFields();
    this.startTracker();
  }

  public componentWillUnmount = async () => {
    await this.stopTracker();
    this.props.clearPage();
  };

  private prepareFilterFields = async () => {
    const { changePageFilter } = this.props;
    const filter = ValueFactory.initializeFilter();
    const fields = await FilterUtil.getFields(ComponentName.ACH);
    changePageFilter({
      filter: filter,
      items: fields,
    });
    this.setState({ filter: FilterUtil.parse(filter) });
  };

  private onChangeFilter = (filter: Filter) => {
    const { rows } = this.state;
    if (_.isEqual(filter, this.state.filter)) return;
    this.setState({ filter, currentPage: 1 });
    this.resubscribeTracker();
  };

  private loadAchFiles = async () => {
    this.resubscribeTracker();
  };

  private startTracker = () => {
    const { filter, rows, currentPage } = this.state;
    const achQuery = QueryBuilder.select()
      .from(CollectionNames.ACH)
      .where(filter)
      .limit(rows)
      .skip((currentPage - 1) * rows)
      .build();

    this.achSubscription = subscribe.Formica.subscribe(CollectionNames.ACH, achQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.achSubscription]);
  };

  private tracker = async () => {
    if (this.achSubscription?.ready()) {
      const { filter, rows, currentPage } = this.state;
      
      const countQuery = QueryBuilder.select().from(CollectionNames.ACH).where(filter).build();
      const totalRecord = await this.achService.getAchFileCount(countQuery);

      const selectQuery = QueryBuilder.select()
        .from(CollectionNames.ACH)
        .where(filter)
        .limit(rows)
        .skip((currentPage - 1) * rows)
        .build();

      const achFiles = await this.achService.getAchFiles(selectQuery);
      this.setState({
        achItems: achFiles,
        totalRecord,
        loading: false,
      });
    }
  };

  private stopTracker = async () => {
    await this.achSubscription?.stop();
    await this.computation?.stop();
  };

  private resubscribeTracker = async () => {
    this.setState({ loading: true });
    if (this.computation == undefined) {
      this.startTracker();
    } else {
      this.computation?.onStop(this.startTracker);
      await this.stopTracker();
    }
  };


  private onPageChange = (event: CustomPaginatorProps) => {
    const { filter } = this.state;
    this.setState({ currentPage: event.page, rows: event.rows });
    this.resubscribeTracker();
  };

  private openImportDialog = () => {
    this.setState({ importDialogVisible: true });
  };

  private closeImportDialog = () => {
    this.setState({ importDialogVisible: false });
  };

  private renderImportDialog = () => {
    const { importDialogVisible, uploading } = this.state;
    return (
      <Dialog
        className={'min-w-[50vh] min-h-[50vh]'}
        visible={importDialogVisible}
        onHide={this.closeImportDialog}
        dismissableMask
        closable
        closeOnEscape
        header={LanguageUtil.get({ id: 'common.label.uploadAchFile', defaultMessage: 'Upload ACH File' })}
      >
        <AchFileUpload onFilesSelect={this.handleFilesSelect} disabled={uploading} dialog={true} />
      </Dialog>
    );
  };

  private checkFileExists = (fileName: string): boolean => {
    return this.state.achItems.some((item: AchItem) => item.fileName === fileName);
  };

  private extractErrorMessage = (error: unknown): string => {
    if (!(error instanceof Error)) {
      return LanguageUtil.get({
        id: 'common.error.unknownError',
        defaultMessage: 'An unknown error occurred',
      });
    }

    const errorMessage = error.message;
    if (errorMessage.includes('already exists')) {
      return LanguageUtil.alreadyExist('ACH', '');
    }

    return LanguageUtil.get({
      id: 'common.error.achFileUploadFailed',
      defaultMessage: 'Failed to upload ACH file',
    });
  };

  private uploadFileSequentially = async (file: File): Promise<{ fileName: string; success: boolean; error?: string }> => {
    const fileName = file.name;

    if (this.checkFileExists(fileName)) {
      return {
        fileName,
        success: false,
        error: LanguageUtil.alreadyExist('ACH', fileName),
      };
    }

    try {
      await this.achService.uploadAchFile(file);
      return {
        fileName,
        success: true,
      };
    } catch (error: unknown) {
      console.error(`Error uploading file ${fileName}:`, error);
      return {
        fileName,
        success: false,
        error: this.extractErrorMessage(error),
      };
    }
  };

  private processFilesSequentially = async (files: File[]): Promise<{ uploaded: string[]; failed: Array<{ fileName: string; error: string }> }> => {
    const uploaded: string[] = [];
    const failed: Array<{ fileName: string; error: string }> = [];

    for (const file of files) {
      const result = await this.uploadFileSequentially(file);
      if (result.success) {
        uploaded.push(result.fileName);
      } else {
        failed.push({
          fileName: result.fileName,
          error: result.error || LanguageUtil.get({
            id: 'common.error.uploadFailed',
            defaultMessage: 'Upload failed',
          }),
        });
      }
    }

    return { uploaded, failed };
  };

  private showUploadResults = (uploaded: string[], failed: Array<{ fileName: string; error: string }>): void => {
    if (uploaded.length > 0) {
      const successMessage = LanguageUtil.get(
        {
          id: 'common.message.filesUploadedSuccessfully',
          defaultMessage: '{count} file(s) uploaded successfully',
        },
        { count: uploaded.length },
      );

      MessageUtil.success({
        summary: LanguageUtil.get({
          id: 'pages.ach.table.achFileName',
          defaultMessage: 'ACH File Name',
        }),
        detail: successMessage,
      });
    }

    if (failed.length > 0) {
      failed.forEach(({ fileName, error }) => {
        MessageUtil.error({
          summary: LanguageUtil.get({
            id: 'pages.ach.table.achFileName',
            defaultMessage: 'ACH File Name',
          }),
          detail: `${fileName}: ${error}`,
        });
      });
    }
  };

  private handleFilesSelect = async (files: File[]): Promise<void> => {
    if (!files || files.length === 0) {
      return;
    }

    this.setState({ uploading: true });

    try {
      const { uploaded, failed } = await this.processFilesSequentially(files);
      this.showUploadResults(uploaded, failed);

      if (uploaded.length > 0) {
        this.closeImportDialog();
        this.resubscribeTracker();
      }
    } catch (error: unknown) {
      console.error('Unexpected error during file upload process:', error);
      MessageUtil.error({
        summary: LanguageUtil.get({
          id: 'common.error.uploadProcessFailed',
          defaultMessage: 'Upload process failed',
        }),
        detail: LanguageUtil.get({
          id: 'common.error.unexpectedError',
          defaultMessage: 'An unexpected error occurred',
        }),
      });
    } finally {
      this.setState({ uploading: false });
    }
  };
}

interface Props extends ReduxProps, FormicaProps {}

const mapStateToProps = (state: RootState) => ({
  page: state.page,
  component: state.component as ComponentState,
});

const mapDispatchToProps = {
  clearPage,
  changePageFilter,
  changePagePath,
  changeActivePageIndex,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  filter: Filter = empty();
  achItems: AchItem[] = [];
  loading: boolean = false;
  importDialogVisible: boolean = false;
  uploading: boolean = false;
  rows: number = 20;
  currentPage: number = 1;
  totalRecord: number = 0;
}

export default withFormica(connector(AchContainer));
